import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { ImportResult, ColumnMapping, Category, DryRunResult } from '../types';
import { apiService } from '../services/api';

interface CSVImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type ImportStep = 'UPLOAD' | 'MAPPING' | 'PREVIEW' | 'IMPORTING' | 'RESULT';

export function CSVImportModal({ isOpen, onClose, onSuccess }: CSVImportModalProps) {
  const [step, setStep] = useState<ImportStep>('UPLOAD');
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<ColumnMapping>({
    nome: null,
    telefone: null,
    email: null,
    observacoes: null,
    tags: null,
    categoriaId: null
  });
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null);
  const [finalResult, setFinalResult] = useState<ImportResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // New Category State
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#3B82F6');

  useEffect(() => {
    if (isOpen) {
      loadCategories();
      resetState();
    }
  }, [isOpen]);

  const resetState = () => {
    setStep('UPLOAD');
    setFile(null);
    setHeaders([]);
    setMapping({});
    setSelectedCategoryId('');
    setDryRunResult(null);
    setFinalResult(null);
    setIsCreatingCategory(false);
    setNewCategoryName('');
  };

  const loadCategories = async () => {
    try {
      const data = await apiService.getAllCategories();
      setCategories(data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      toast.error('Erro ao carregar categorias');
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      const newCat = await apiService.createCategory({
        nome: newCategoryName,
        cor: newCategoryColor,
        descricao: 'Criada via importação CSV'
      });

      setCategories(prev => [...prev, newCat]);
      setSelectedCategoryId(newCat.id);
      setIsCreatingCategory(false);
      setNewCategoryName('');
      toast.success('Categoria criada!');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao criar categoria.');
    }
  };

  // Step 1: Upload & Analyze Headers (Dry Run 1)
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
      toast.error('Por favor, selecione um arquivo CSV válido');
      return;
    }

    setFile(selectedFile);
    setIsLoading(true);

    try {
      // Faz um dry-run inicial sem mapeamento para pegar os headers
      const result = await apiService.importCSV(selectedFile, { mapping: {}, dryRun: true }) as DryRunResult;

      if (result.headers && result.headers.length > 0) {
        setHeaders(result.headers);
        // Tenta automapear
        const autoMap: ColumnMapping = {
          nome: findHeader(result.headers, ['nome', 'name', 'full_name']),
          telefone: findHeader(result.headers, ['telefone', 'phone', 'celular', 'mobile', 'whatsapp']),
          email: findHeader(result.headers, ['email', 'e-mail', 'mail']),
          observacoes: findHeader(result.headers, ['observacoes', 'obs', 'notes', 'comments']),
          tags: findHeader(result.headers, ['tags', 'tag', 'labels']),
          categoriaId: findHeader(result.headers, ['categoriaid', 'categoria', 'category'])
        };
        setMapping(autoMap);
        setStep('MAPPING');
      } else {
        toast.error('Não foi possível ler os cabeçalhos do arquivo.');
        setFile(null);
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao ler arquivo CSV.');
      setFile(null);
    } finally {
      setIsLoading(false);
    }
  };

  const findHeader = (headers: string[], aliases: string[]) => {
    return headers.find(h => aliases.includes(h.toLowerCase())) || null;
  };

  // Step 2: Mapping -> Preview (Dry Run 2)
  const handleProceedToPreview = async () => {
    if (!file) return;
    if (!mapping.nome || !mapping.telefone) {
      toast.error('Por favor, mapeie pelo menos as colunas de "Nome" e "Telefone".');
      return;
    }
    if (!selectedCategoryId) {
      toast.error('Por favor, selecione uma categoria.');
      return;
    }

    setIsLoading(true);
    try {
      const result = await apiService.importCSV(file, {
        mapping,
        category: { mode: 'fixedId', id: selectedCategoryId },
        dryRun: true
      }) as DryRunResult;

      setDryRunResult(result);
      setStep('PREVIEW');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao validar dados.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Final Import
  const handleFinalImport = async () => {
    if (!file || !dryRunResult) return;

    setStep('IMPORTING');
    setIsLoading(true);

    try {
      const result = await apiService.importCSV(file, {
        mapping,
        category: { mode: 'fixedId', id: selectedCategoryId }, // Envia string, backend vai precisar lidar ou quebrar
        dryRun: false
      }) as ImportResult;

      setFinalResult(result);
      setStep('RESULT');
      if (result.success) {
        if (result.failedImports > 0) {
          toast('Importação concluída: alguns registros foram ignorados.', { icon: '⚠️' });
        } else {
          toast.success('Importação concluída com sucesso!');
        }
        onSuccess();
      } else {
        toast('Importação concluída com erros.', { icon: '⚠️' });
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro na importação final.');
      setStep('PREVIEW'); // Voltar para preview em caso de erro fatal
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await apiService.downloadCSVTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template-contatos.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar template');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#0f172a]/80 flex items-center justify-center p-4 z-50 backdrop-blur-md">
      <div className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] rounded-[2.5rem] shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col border border-white/10 backdrop-blur-md">
        {/* Header */}
        <div className="bg-white/5 p-6 border-b border-white/10 backdrop-blur-md">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600/20 to-blue-900/20 rounded-2xl flex items-center justify-center border border-blue-500/20 shadow-inner group">
                <svg className="w-8 h-8 text-blue-400 group-hover:scale-110 transition-transform duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              </div>
              <div>
                <h2 className="text-3xl font-bold font-oswald text-white uppercase tracking-tight">Importar Contatos</h2>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] font-oswald mt-2">Importe sua lista de contatos via arquivo CSV</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-4 text-gray-400 hover:text-white hover:bg-white/10 rounded-2xl transition-all border border-white/5 hover:border-white/10"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Stepper */}
          <div className="mt-6 flex items-center gap-3">
            {(['UPLOAD', 'MAPPING', 'PREVIEW', 'RESULT'] as const).map((s, index) => {
              const isActive = step === s || (step === 'IMPORTING' && s === 'PREVIEW');
              const isPast = index < ['UPLOAD', 'MAPPING', 'PREVIEW', 'RESULT'].indexOf(step === 'IMPORTING' ? 'PREVIEW' : step);
              
              return (
                <div key={s} className="flex-1 flex items-center gap-4">
                  <div className={`flex items-center gap-3 ${isActive ? 'text-white' : isPast ? 'text-blue-400' : 'text-gray-600'}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold font-oswald text-xs border transition-all duration-500 ${
                      isActive ? 'bg-blue-600 border-blue-500 shadow-lg shadow-blue-900/40' :
                      isPast ? 'bg-blue-500/10 border-blue-500/20' :
                      'bg-white/5 border-white/10'
                    }`}>
                      {isPast ? (
                        <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : index + 1}
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] font-oswald whitespace-nowrap">
                      {s === 'UPLOAD' ? 'Arquivo' : s === 'MAPPING' ? 'Mapeamento' : s === 'PREVIEW' ? 'Validação' : 'Resultado'}
                    </span>
                  </div>
                  {index < 3 && (
                    <div className="flex-1 h-[2px] bg-white/5 rounded-full overflow-hidden min-w-[20px]">
                      <div className={`h-full bg-blue-500 transition-all duration-1000 ${isPast ? 'w-full' : 'w-0'}`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-hidden p-6">
          {step === 'UPLOAD' && (
            <div className="flex flex-col items-center justify-center py-10 gap-8">
              {/* Template Card */}
              <div className="w-full max-w-xl bg-blue-500/5 rounded-3xl p-8 flex justify-between items-center border border-blue-500/10 backdrop-blur-md">
                <div className="flex items-center gap-6">
                  <div className="w-14 h-14 bg-blue-500/10 rounded-2xl flex items-center justify-center border border-blue-500/20">
                    <svg className="w-7 h-7 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white font-oswald uppercase tracking-[0.2em]">Template CSV</h3>
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1">Baixe o modelo para referência</p>
                  </div>
                </div>
                <button
                  onClick={handleDownloadTemplate}
                  className="px-8 py-3 bg-white/5 text-blue-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-xl hover:bg-white/10 transition-all border border-white/5 hover:border-blue-500/30 font-oswald"
                >
                  Baixar Modelo
                </button>
              </div>

              {/* Upload Area */}
              <div className="w-full max-w-xl">
                <label className="relative group cursor-pointer">
                  <input
                    id="csv-file"
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div className="flex flex-col items-center justify-center p-16 bg-white/5 rounded-[2.5rem] border-2 border-dashed border-white/10 group-hover:border-blue-500/50 group-hover:bg-blue-500/5 transition-all duration-500 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    <div className="w-20 h-20 bg-blue-500/10 rounded-full flex items-center justify-center mb-6 border border-blue-500/20 group-hover:scale-110 transition-transform duration-500">
                      {isLoading ? (
                        <div className="w-8 h-8 rounded-full border-2 border-blue-500/20 border-t-blue-500 animate-spin" />
                      ) : (
                        <svg className="w-10 h-10 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      )}
                    </div>
                    
                    <h3 className="text-xl font-bold text-white font-oswald uppercase tracking-tight mb-2">
                      {isLoading ? 'Analisando arquivo...' : 'Arraste seu arquivo CSV'}
                    </h3>
                    <p className="text-gray-500 text-[10px] font-bold uppercase tracking-widest text-center">
                      Ou clique para navegar em seus arquivos
                    </p>
                    
                    <div className="mt-8 px-6 py-2 bg-white/5 rounded-full border border-white/10 text-[9px] font-bold text-gray-500 uppercase tracking-[0.2em] font-oswald">
                      Apenas arquivos .CSV são suportados
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {step === 'MAPPING' && (
            <div className="flex flex-col gap-6 justify-between min-h-[60vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Column Mapping Section */}
                <div className="space-y-6">
                  <div className="flex items-center gap-4 mb-2">
                    <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center border border-blue-500/20">
                      <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-white font-oswald uppercase tracking-tight">Mapear Colunas</h3>
                  </div>

                  <div className="grid grid-cols-1 gap-4 p-4 bg-white/5 rounded-[2.5rem] border border-white/10">
                    {[
                      { key: 'nome', label: 'Nome *' },
                      { key: 'telefone', label: 'Telefone *' },
                      { key: 'email', label: 'E-mail' },
                      { key: 'tags', label: 'Tags (separadas por vírgula)' },
                      { key: 'observacoes', label: 'Observações' }
                    ].map(({ key, label }) => (
                      <div key={key} className="space-y-3">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">
                          {label}
                        </label>
                        <div className="relative">
                          <select
                            value={mapping[key as keyof ColumnMapping] || ''}
                            onChange={(e) => setMapping({ ...mapping, [key]: e.target.value || null })}
                            className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all font-oswald font-bold text-[10px] tracking-[0.2em] cursor-pointer"
                          >
                            <option value="" className="bg-[#1e293b]">-- Ignorar --</option>
                            {headers.map(h => (
                              <option key={h} value={h} className="bg-[#1e293b]">{h}</option>
                            ))}
                          </select>
                          <div className="absolute inset-y-0 right-6 flex items-center pointer-events-none text-gray-500">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Category Selection Section */}
                <div className="space-y-6">
                  <div className="flex items-center gap-4 mb-2">
                    <div className="w-10 h-10 bg-purple-500/10 rounded-xl flex items-center justify-center border border-purple-500/20">
                      <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-white font-oswald uppercase tracking-tight">Categoria de Destino</h3>
                  </div>

                  <div className="p-4 bg-white/5 rounded-[2.5rem] border border-white/10 space-y-6">
                    {!isCreatingCategory ? (
                      <div className="space-y-6">
                        <div className="space-y-3">
                          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">
                            Selecionar Categoria Existente
                          </label>
                          <div className="relative">
                            <select
                              value={selectedCategoryId}
                              onChange={(e) => setSelectedCategoryId(e.target.value)}
                              className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all font-oswald font-bold text-[10px] tracking-[0.2em] cursor-pointer"
                            >
                              <option value="" className="bg-[#1e293b]">Escolha uma categoria...</option>
                              {categories.map(c => (
                                <option key={c.id} value={c.id} className="bg-[#1e293b]">{c.nome}</option>
                              ))}
                            </select>
                            <div className="absolute inset-y-0 right-6 flex items-center pointer-events-none text-gray-500">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="flex-1 h-[1px] bg-white/5" />
                          <span className="text-[9px] font-bold text-gray-600 uppercase tracking-widest">OU</span>
                          <div className="flex-1 h-[1px] bg-white/5" />
                        </div>

                        <button
                          onClick={() => setIsCreatingCategory(true)}
                          className="w-full py-3 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl hover:bg-white/10 transition-all border border-white/5 hover:border-white/10 font-oswald flex items-center justify-center gap-3"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Criar Nova Categoria
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-6 animate-fade-in">
                        <div className="grid grid-cols-4 gap-4">
                          <div className="col-span-3 space-y-3">
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">
                              Nome da Nova Categoria
                            </label>
                            <input
                              type="text"
                              value={newCategoryName}
                              onChange={(e) => setNewCategoryName(e.target.value)}
                              placeholder="EX: IMPORTADOS CSV"
                              className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all font-oswald font-bold text-[10px] tracking-[0.2em]"
                            />
                          </div>
                          <div className="space-y-3">
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">
                              Cor
                            </label>
                            <input
                              type="color"
                              value={newCategoryColor}
                              onChange={(e) => setNewCategoryColor(e.target.value)}
                              className="w-full h-[54px] bg-white/5 border border-white/5 rounded-2xl p-2 cursor-pointer"
                            />
                          </div>
                        </div>
                        
                        <div className="flex gap-3">
                          <button
                            onClick={handleCreateCategory}
                            disabled={!newCategoryName.trim()}
                            className="flex-1 py-3 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-lg shadow-blue-900/40 hover:scale-105 transition-all duration-300 border border-blue-500 font-oswald disabled:opacity-50"
                          >
                            Criar e Selecionar
                          </button>
                          <button
                            onClick={() => setIsCreatingCategory(false)}
                            className="px-6 py-3 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl hover:bg-white/10 transition-all border border-white/5 font-oswald"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  onClick={resetState}
                  className="px-6 py-3 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl hover:bg-white/10 transition-all border border-white/5 font-oswald"
                >
                  Voltar
                </button>
                <button
                  onClick={handleProceedToPreview}
                  disabled={isLoading}
                  className="px-8 py-3 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-lg shadow-blue-900/40 hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 border border-blue-500 relative overflow-hidden group/btn font-oswald disabled:opacity-50"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                  <span>{isLoading ? 'Validando...' : 'Avançar'}</span>
                </button>
              </div>
            </div>
          )}

          {step === 'PREVIEW' && dryRunResult && (
            <div className="space-y-8 animate-fade-in">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center border border-blue-500/20">
                    <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold font-oswald text-white uppercase tracking-tight">Pré-visualização</h3>
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] font-oswald mt-2">
                      Verifique os dados antes de processar
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="px-6 py-4 bg-white/5 rounded-2xl border border-white/5 text-center">
                    <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Total</div>
                    <div className="text-xl font-bold text-white font-oswald">{dryRunResult.totalRows}</div>
                  </div>
                  <div className="px-6 py-4 bg-green-500/10 rounded-2xl border border-green-500/20 text-center">
                    <div className="text-[10px] font-bold text-green-500/70 uppercase tracking-widest mb-1">Válidos</div>
                    <div className="text-xl font-bold text-green-400 font-oswald">{dryRunResult.validRows}</div>
                  </div>
                  {dryRunResult.invalidRows > 0 && (
                    <div className="px-6 py-4 bg-red-500/10 rounded-2xl border border-red-500/20 text-center">
                      <div className="text-[10px] font-bold text-red-500/70 uppercase tracking-widest mb-1">Inválidos</div>
                      <div className="text-xl font-bold text-red-400 font-oswald">{dryRunResult.invalidRows}</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white/5 rounded-[2.5rem] border border-white/10 overflow-hidden backdrop-blur-md">
                <div className="max-h-[400px] overflow-auto custom-scrollbar">
                  <table className="w-full border-separate border-spacing-0">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-[#1e293b]/80 backdrop-blur-md">
                        <th className="px-8 py-5 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald border-b border-white/10">Status</th>
                        <th className="px-8 py-5 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald border-b border-white/10">Nome</th>
                        <th className="px-8 py-5 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald border-b border-white/10">Telefone</th>
                        <th className="px-8 py-5 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald border-b border-white/10">E-mail</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {dryRunResult.preview.map((row, idx) => (
                        <tr key={idx} className="group hover:bg-white/[0.02] transition-colors">
                          <td className="px-8 py-4 whitespace-nowrap">
                            {row.issues.length === 0 ? (
                              <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center border border-green-500/20">
                                <svg className="w-3.5 h-3.5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center border border-red-500/20" title={row.issues.map(i => i.message).join(', ')}>
                                <svg className="w-3.5 h-3.5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </div>
                            )}
                          </td>
                          <td className="px-8 py-4 whitespace-nowrap text-sm font-bold text-white font-oswald uppercase tracking-wide">{row.data.nome}</td>
                          <td className="px-8 py-4 whitespace-nowrap text-[10px] font-bold text-gray-400 font-oswald tracking-widest">{row.data.telefone}</td>
                          <td className="px-8 py-4 whitespace-nowrap text-[10px] font-bold text-gray-500 uppercase tracking-widest">{row.data.email || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-6">
                <button
                  onClick={() => setStep('MAPPING')}
                  className="px-8 py-4 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl hover:bg-white/10 transition-all border border-white/5 font-oswald"
                >
                  Corrigir Mapeamento
                </button>
                <button
                  onClick={handleFinalImport}
                  disabled={dryRunResult.validRows === 0}
                  className="px-10 py-4 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-lg shadow-blue-900/40 hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 border border-blue-500 relative overflow-hidden group/btn font-oswald disabled:opacity-50"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                  <span>Confirmar Importação</span>
                </button>
              </div>
            </div>
          )}

          {step === 'IMPORTING' && (
            <div className="flex flex-col items-center justify-center py-32 gap-8">
              <div className="relative">
                <div className="w-24 h-24 rounded-full border-4 border-blue-500/20 border-t-blue-500 animate-spin" />
                <div className="absolute inset-0 bg-blue-500/20 rounded-full blur-2xl animate-pulse" />
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold font-oswald text-white uppercase tracking-tight mb-3">Processando Importação</h3>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] font-oswald animate-pulse">
                  Por favor, aguarde enquanto processamos sua lista...
                </p>
              </div>
            </div>
          )}

          {step === 'RESULT' && finalResult && (
            <div className="flex flex-col items-center justify-center py-10 gap-6 animate-fade-in text-center">
              <div className={`w-24 h-24 rounded-[2rem] flex items-center justify-center border shadow-2xl transition-all duration-1000 ${
                finalResult.success ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'
              }`}>
                {finalResult.success ? (
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>

              <div className="space-y-4">
                <h3 className="text-3xl font-bold font-oswald text-white uppercase tracking-tight">
                  {finalResult.success ? 'Importação Concluída!' : 'Ocorreu um Erro'}
                </h3>
                <p className="text-gray-400 text-sm max-w-md mx-auto leading-relaxed">
                  {finalResult.success
                    ? (finalResult.failedImports > 0
                        ? `Importação concluída com avisos. ${finalResult.failedImports} registro(s) ignorado(s).`
                        : `Processamos seu arquivo com sucesso. Confira o resumo abaixo.`)
                    : `Não foi possível completar a importação. Verifique os erros e tente novamente.`}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-6 w-full max-w-2xl">
                <div className="p-6 bg-white/5 rounded-2xl border border-white/5">
                  <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Total</div>
                  <div className="text-2xl font-bold text-white font-oswald">{finalResult.totalRows}</div>
                </div>
                <div className="p-6 bg-green-500/10 rounded-2xl border border-green-500/20">
                  <div className="text-[10px] font-bold text-green-500/70 uppercase tracking-widest mb-1">Sucesso</div>
                  <div className="text-2xl font-bold text-green-400 font-oswald">{finalResult.successfulImports}</div>
                </div>
                <div className="p-6 bg-red-500/10 rounded-2xl border border-red-500/20">
                  <div className="text-[10px] font-bold text-red-500/70 uppercase tracking-widest mb-1">Falhas</div>
                  <div className="text-2xl font-bold text-red-400 font-oswald">{finalResult.failedImports}</div>
                </div>
              </div>

              {finalResult.errors && finalResult.errors.length > 0 && (
                <div className="w-full max-w-2xl mt-4">
                  <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-6 text-left">
                    <h4 className="text-[10px] font-bold text-red-400 uppercase tracking-[0.2em] font-oswald mb-4 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Detalhes dos Erros
                    </h4>
                    <div className="space-y-2">
                      {finalResult.errors.map((error, idx) => (
                        <p key={idx} className="text-[10px] text-gray-400 font-medium">
                          • {error}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={handleClose}
                className="px-12 py-4 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-lg shadow-blue-900/40 hover:scale-105 transition-all duration-300 border border-blue-500 relative overflow-hidden group/btn font-oswald"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                Fechar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
